/**
 * Process-wide singleton wiring for the analytics spine.
 *
 * The module-level `track` / `aiFeedback` / `reportBug` functions delegate to a
 * lazily-created singleton client. This lets any code call them without a React
 * provider in scope (e.g. deep utilities), while `initAnalytics` attaches the
 * real transport (a `ServerApiClient`) and PostHog config once the app boots.
 * Calls made before init are buffered, not lost.
 */

import { AnalyticsClient } from './client'
import type {
  AiFeedbackInput,
  AnalyticsTransport,
  BugReportInput,
  PostHogConfig,
  TrackInput,
} from './types'

let singleton: AnalyticsClient | null = null

export function getAnalytics(): AnalyticsClient {
  if (!singleton) {
    singleton = new AnalyticsClient({ posthog: readPostHogConfigFromEnv() })
  }
  return singleton
}

/** Read the env-gated PostHog config (no-op when `VITE_POSTHOG_KEY` is unset). */
export function readPostHogConfigFromEnv(): PostHogConfig {
  const env: Record<string, string | undefined> =
    (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string | undefined> }).env) || {}
  return {
    key: env.VITE_POSTHOG_KEY ?? null,
    host: env.VITE_POSTHOG_HOST ?? null,
  }
}

export interface InitAnalyticsConfig {
  transport?: AnalyticsTransport | null
  posthog?: PostHogConfig | null
  distinctId?: string | null
}

/** Attach/refresh the transport, PostHog config, and distinct id. Idempotent. */
export function initAnalytics(config: InitAnalyticsConfig): AnalyticsClient {
  const client = getAnalytics()
  if ('transport' in config) client.setTransport(config.transport ?? null)
  if (config.posthog) client.setPostHog(config.posthog)
  if (typeof config.distinctId !== 'undefined') client.setUser(config.distinctId)
  return client
}

export function track(input: TrackInput): void {
  getAnalytics().track(input)
}

export function aiFeedback(input: AiFeedbackInput): void {
  getAnalytics().aiFeedback(input)
}

export function reportBug(input: BugReportInput): void {
  getAnalytics().reportBug(input)
}

export function flushAnalytics(): Promise<void> {
  return getAnalytics().flush()
}

/** Test-only: drop the singleton so each test starts clean. */
export function resetAnalyticsForTests(): void {
  singleton?.destroy()
  singleton = null
}
